/**
 * The pinned row set (B-254, P2-2 part A, Phase 3; spec v1.1 §4).
 *
 * Bools are sortable, groupable and filterable, so a tick under "FOUND is not checked" would
 * pull the row out from under Bob's thumb. On the first tap the host freezes what is on screen,
 * and holds it until the rep asks for a different view.
 *
 * Pure functions; the host owns the state. Each guards a trap with a wrong version that looks
 * right:
 *   - IDS, NOT ROWS. The pin holds record_ids and every render looks them up in the live
 *     `records`. A frozen array of rows would freeze the ticked cell's own value.
 *   - A TREE, NOT A FLAT ORDER, when grouped. groupRecords buckets by the live value, so a
 *     pinned order fed back through it would still move a ticked row to another bucket. Bucket
 *     values and ids are pinned; counts are re-derived from the rows that still resolve.
 *   - A DISCARDED ID IS SKIPPED. A pinned id with no row (a Discard) renders nothing, never a
 *     hole or a throw. A bucket left with no rows is dropped, as groupRecords never makes one.
 *   - A RE-KEY IS REMAPPED, by the caller, only for pairs it actually applies (remapPin).
 *
 * Release is mostly derived: the pin carries the view key it was made under, and applies only
 * while the current key matches. A filter, sort or group change, the INV/ALL switch, or a
 * different site makes a new key, so the pin stops applying in that same render. The host
 * releases explicitly on leaving the site and on a create (D-10).
 */

/** The view a pin was made under. Anything here changing releases the pin. */
export function pinKey({ siteId, fieldView, filter, filterLogic, sort, group }) {
  return JSON.stringify([siteId ?? null, fieldView ?? null, filter ?? null, filterLogic ?? null, sort ?? null, group ?? null]);
}

const idsOf = (rows) => rows.map((r) => r.record_id);

function bucketIds(bucket) {
  return {
    value: bucket.value,
    ids: bucket.rows ? idsOf(bucket.rows) : null,
    children: bucket.children ? bucket.children.map(bucketIds) : null,
  };
}

/** Freeze the view on screen: its flat order, and its bucket tree when grouped (else null). */
export function makePin(key, rows, tree) {
  return { key, ids: idsOf(rows), tree: tree ? tree.map(bucketIds) : null };
}

const byId = (records) => new Map(records.map((r) => [r.record_id, r]));

const resolve = (ids, index) => ids.map((id) => index.get(id)).filter(Boolean);

/** The pinned flat order, as live rows. A pinned id with no row is skipped. */
export function pinnedRows(pin, records) {
  return resolve(pin.ids, byId(records));
}

function materialise(bucket, index) {
  if (bucket.ids) {
    const rows = resolve(bucket.ids, index);
    return rows.length ? { value: bucket.value, count: rows.length, rows, children: null } : null;
  }
  const children = bucket.children.map((c) => materialise(c, index)).filter(Boolean);
  if (!children.length) return null;
  const count = children.reduce((n, c) => n + c.count, 0);
  return { value: bucket.value, count, rows: null, children };
}

/** The pinned bucket tree, in groupRecords' shape, as live rows; null when the pin is flat. */
export function pinnedTree(pin, records) {
  if (!pin.tree) return null;
  const index = byId(records);
  return pin.tree.map((b) => materialise(b, index)).filter(Boolean);
}

function remapIds(ids, map) {
  let changed = false;
  const next = ids.map((id) => {
    if (map.has(id)) { changed = true; return map.get(id); }
    return id;
  });
  return changed ? next : ids;
}

function remapBucket(bucket, map) {
  const ids = bucket.ids ? remapIds(bucket.ids, map) : null;
  const children = bucket.children ? bucket.children.map((c) => remapBucket(c, map)) : null;
  const same = ids === bucket.ids && (!children || children.every((c, i) => c === bucket.children[i]));
  return same ? bucket : { ...bucket, ids, children };
}

/**
 * Replace re-keyed pending ids with their real ids, keeping their place. `pairs` are
 * reconcile's { pending_id, record } pairs, and must be ONLY the ones being applied to
 * `records` in the same batch. Remapping a pair that is still stashed or deferred would point
 * the pin at an id `records` does not hold yet, and the row would vanish. Returns the same pin
 * when nothing changes, and null for no pin.
 */
export function remapPin(pin, pairs) {
  if (!pin) return pin;
  const map = new Map();
  for (const p of pairs) if (p && p.pending_id && p.record) map.set(p.pending_id, p.record.record_id);
  if (!map.size) return pin;
  const ids = remapIds(pin.ids, map);
  const tree = pin.tree ? pin.tree.map((b) => remapBucket(b, map)) : null;
  const same = ids === pin.ids && (!tree || tree.every((b, i) => b === pin.tree[i]));
  return same ? pin : { ...pin, ids, tree };
}
